import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const STORAGE_KEY = "scudieri_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(consent) {
  const payload = { ...consent, ts: new Date().toISOString(), version: 1 };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // apply to PostHog if loaded
  try {
    if (window.posthog) {
      if (consent.analytics) {
        window.posthog.opt_in_capturing && window.posthog.opt_in_capturing();
      } else {
        window.posthog.opt_out_capturing && window.posthog.opt_out_capturing();
      }
    }
  } catch (_) {}
  return payload;
}

export function applyStoredConsent() {
  const c = readConsent();
  // Default-deny analytics if no consent yet (Italian GDPR / Garante guidelines)
  try {
    if (window.posthog && window.posthog.opt_out_capturing) {
      if (c?.analytics) {
        window.posthog.opt_in_capturing && window.posthog.opt_in_capturing();
      } else {
        window.posthog.opt_out_capturing();
      }
    }
  } catch (_) {}
}

const COPY = {
  it: {
    title: "Cookie & Privacy",
    body:
      "Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie di analisi per migliorare l'esperienza. Puoi accettare tutti, rifiutare i non necessari o personalizzare le scelte. Maggiori informazioni nella nostra",
    policyLink: "Cookie Policy",
    privacyLink: "Privacy Policy",
    acceptAll: "Accetta tutti",
    rejectAll: "Rifiuta",
    customize: "Personalizza",
    save: "Salva preferenze",
    cancel: "Annulla",
    necessary: "Cookie necessari",
    necessaryDesc:
      "Indispensabili al funzionamento del sito (navigazione, lingua selezionata, area riservata). Sempre attivi.",
    analytics: "Cookie analitici",
    analyticsDesc:
      "Ci aiutano a capire come viene utilizzato il sito in forma aggregata e anonima per migliorarne le funzionalità.",
    alwaysOn: "Sempre attivi",
    manage: "Gestisci cookie",
  },
  en: {
    title: "Cookies & Privacy",
    body:
      "We use technical cookies necessary for the site to work and, with your consent, analytics cookies to improve your experience. You can accept all, reject the optional ones or customise your choices. More info in our",
    policyLink: "Cookie Policy",
    privacyLink: "Privacy Policy",
    acceptAll: "Accept all",
    rejectAll: "Reject",
    customize: "Customize",
    save: "Save preferences",
    cancel: "Cancel",
    necessary: "Necessary cookies",
    necessaryDesc:
      "Essential for the site to operate (navigation, selected language, admin area). Always active.",
    analytics: "Analytics cookies",
    analyticsDesc:
      "Help us understand how the site is used in aggregate and anonymous form to improve its features.",
    alwaysOn: "Always on",
    manage: "Manage cookies",
  },
};

export default function CookieBanner() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.it;
  const [open, setOpen] = React.useState(false);
  const [showPanel, setShowPanel] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(false);

  React.useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setAnalytics(!!existing.analytics);
    }
    applyStoredConsent();
    // expose a global helper to reopen the panel from footer link
    window.__openCookieSettings = () => {
      const cur = readConsent();
      setAnalytics(!!cur?.analytics);
      setShowPanel(true);
      setOpen(true);
    };
    return () => {
      try { delete window.__openCookieSettings; } catch (_) {}
    };
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true });
    setOpen(false);
    setShowPanel(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false });
    setOpen(false);
    setShowPanel(false);
  };
  const savePrefs = () => {
    writeConsent({ necessary: true, analytics });
    setOpen(false);
    setShowPanel(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] p-3 md:p-5"
      role="dialog"
      aria-labelledby="cookie-title"
      data-testid="cookie-banner"
    >
      <div className="container-x">
        <div className="relative bg-[#0e0e0e] border border-[color:var(--gold)]/40 shadow-[0_24px_60px_rgba(0,0,0,0.7)]">
          {/* Compact banner */}
          {!showPanel && (
            <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-8 flex gap-4">
                <div className="hidden md:flex w-11 h-11 border border-[color:var(--gold)] items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-[color:var(--gold)]" />
                </div>
                <div>
                  <h3
                    id="cookie-title"
                    className="font-display tracking-[0.18em] uppercase text-[color:var(--gold)] text-sm"
                  >
                    {c.title}
                  </h3>
                  <p className="text-[color:var(--txt-secondary)] text-sm leading-relaxed mt-3">
                    {c.body}{" "}
                    <Link to="/cookie" className="link-gold underline underline-offset-4 text-white">
                      {c.policyLink}
                    </Link>{" "}
                    ·{" "}
                    <Link to="/privacy" className="link-gold underline underline-offset-4 text-white">
                      {c.privacyLink}
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end">
                <button
                  onClick={() => setShowPanel(true)}
                  className="btn-ghost !py-3 !px-4 text-[10px]"
                  data-testid="cookie-customize"
                >
                  {c.customize}
                </button>
                <button
                  onClick={rejectAll}
                  className="btn-ghost !py-3 !px-4 text-[10px]"
                  data-testid="cookie-reject"
                >
                  {c.rejectAll}
                </button>
                <button
                  onClick={acceptAll}
                  className="btn-primary !py-3 !px-4 text-[10px]"
                  data-testid="cookie-accept-all"
                >
                  {c.acceptAll}
                </button>
              </div>
            </div>
          )}

          {/* Detail panel */}
          {showPanel && (
            <div className="p-6 md:p-8" data-testid="cookie-panel">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-display tracking-[0.18em] uppercase text-[color:var(--gold)] text-sm">
                    {c.title}
                  </h3>
                  <p className="text-[color:var(--txt-secondary)] text-sm leading-relaxed mt-3 max-w-2xl">
                    {c.body}{" "}
                    <Link to="/cookie" className="link-gold underline underline-offset-4 text-white">
                      {c.policyLink}
                    </Link>
                    .
                  </p>
                </div>
                <button
                  onClick={() => setShowPanel(false)}
                  className="text-white/50 hover:text-white p-2"
                  aria-label="Chiudi"
                  data-testid="cookie-panel-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <Category
                  icon={<Shield className="w-4 h-4 text-[color:var(--gold)]" />}
                  title={c.necessary}
                  desc={c.necessaryDesc}
                  rightLabel={c.alwaysOn}
                  locked
                />
                <Category
                  icon={<BarChart3 className="w-4 h-4 text-[color:var(--gold)]" />}
                  title={c.analytics}
                  desc={c.analyticsDesc}
                  active={analytics}
                  onToggle={() => setAnalytics((v) => !v)}
                  testid="cookie-toggle-analytics"
                />
              </div>

              <div className="mt-7 flex flex-wrap gap-3 justify-end">
                <button onClick={rejectAll} className="btn-ghost text-[10px]" data-testid="cookie-panel-reject">
                  {c.rejectAll}
                </button>
                <button onClick={savePrefs} className="btn-secondary text-[10px]" data-testid="cookie-panel-save">
                  {c.save}
                </button>
                <button onClick={acceptAll} className="btn-primary text-[10px]" data-testid="cookie-panel-accept-all">
                  {c.acceptAll}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Category({ icon, title, desc, active, onToggle, locked, rightLabel, testid }) {
  return (
    <div className="border border-white/10 p-5 flex items-start gap-4">
      <div className="w-9 h-9 border border-[color:var(--gold)]/40 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-display tracking-[0.16em] uppercase text-white text-xs">{title}</div>
        <p className="text-[color:var(--txt-secondary)] text-sm leading-relaxed mt-2">{desc}</p>
      </div>
      <div className="shrink-0">
        {locked ? (
          <span className="badge-pill text-[color:var(--gold)]">{rightLabel}</span>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className={`w-12 h-6 relative transition-colors ${active ? "bg-[color:var(--gold)]" : "bg-white/10"}`}
            aria-pressed={active}
            data-testid={testid}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-black transition-all ${active ? "left-6" : "left-0.5"}`} />
          </button>
        )}
      </div>
    </div>
  );
}
